import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  Row,
  Col,
  Card,
  Input,
  Select,
  Slider,
  Typography,
  Space,
  Tag,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { categoriesTrans, allBrands, levels, styles } from '../../constants';
import graphqlService from '../../services/graphql';
import { useProductListData } from '../../hooks/useProductListData';
import { useSearchData } from '../../hooks/useSearchData';
import { getBrandImage } from '../../utils/brandImages';
import OptimizedImage from '../../components/OptimizedImage';
import { CardSkeleton } from '../../components/Skeleton';

// 导入样式组件
import {
  StyledCard,
  FilterBar,
  FilterRow,
  FilterLabel,
  FilterTag,
  CardSection,
  RecommendSection,
  BrandSection,
  BrandList,
  AlphaIndex,
  BrandGroupTitle,
  BrandItem,
  CardImage
} from '../../components/Home/styles';


const { Title } = Typography;

const FilterSection = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ProductGrid = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const SearchSection = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

// 从常量中获取品牌列表
const brands = allBrands.map(brand => brand.brandName);

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const ProductList = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const selectedCategory = query.get('category');
  const selectedBrand = query.get('brand');
  const nameParam = query.get('name'); // 老逻辑：完整过滤功能中的搜索装备名称
  const searchTextParam = query.get('searchtext'); // 新逻辑：从搜索页面跳转的多字段搜索
  const styleParam = query.get('style');
  const minPriceParam = query.get('minPrice');
  const maxPriceParam = query.get('maxPrice');

  // 判断是否为搜索模式（从搜索页面跳转过来）
  // 搜索模式：检查是否有from=search参数
  const isSearchMode = query.get('from') === 'search';

  // 搜索模式：使用简化的搜索数据源
  const searchData = useSearchData();
  
  // 普通模式：使用完整的产品列表数据源
  const productData = useProductListData();

  // 根据模式选择数据源
  const {
    loading,
    initialLoading,
    showSkeleton, // 新增骨架屏显示状态
    equipments,
    loadMore,
    hasMore,
    searchText: currentSearchText,
    setSearchText,
    selectedBrands: currentSelectedBrands,
    setSelectedBrands,
    selectedCategories: currentSelectedCategories,
    setSelectedCategories,
    selectedStyles: currentSelectedStyles,
    setSelectedStyles,
    priceRange: currentPriceRange,
    setPriceRange
  } = isSearchMode ? {
    // 搜索模式：支持多字段搜索和分页
    loading: searchData.loading,
    initialLoading: false, // 搜索模式不使用initialLoading
    showSkeleton: searchData.showSkeleton, // 使用搜索模式的骨架屏状态
    equipments: searchData.equipments,
    loadMore: searchData.loadMore,
    hasMore: searchData.hasMore,
    searchText: searchData.searchText,
    setSearchText: searchData.setSearchText,
    selectedBrands: [],
    setSelectedBrands: () => {},
    selectedCategories: [],
    setSelectedCategories: () => {},
    selectedStyles: [],
    setSelectedStyles: () => {},
    priceRange: [0, 10000],
    setPriceRange: () => {}
  } : {
    // 普通模式：完整的过滤功能
    loading: productData.loading,
    initialLoading: productData.initialLoading,
    showSkeleton: productData.showSkeleton, // 使用新的骨架屏状态
    equipments: productData.equipments,
    loadMore: productData.loadMore,
    hasMore: productData.hasMore,
    searchText: productData.searchText,
    setSearchText: productData.setSearchText,
    selectedBrands: productData.selectedBrands,
    setSelectedBrands: productData.setSelectedBrands,
    selectedCategories: productData.selectedCategories,
    setSelectedCategories: productData.setSelectedCategories,
    selectedStyles: productData.selectedStyles,
    setSelectedStyles: productData.setSelectedStyles,
    priceRange: productData.priceRange,
    setPriceRange: productData.setPriceRange
  };

  // 初始化 URL 参数到搜索状态
  useEffect(() => {
    // 设置标志，表示正在从URL更新状态
    isUpdatingFromURL.current = true;
    
    if (isSearchMode) {
      // 搜索模式：只处理搜索文本
      const urlSearchText = searchTextParam || '';
      if (urlSearchText !== currentSearchText) {
        setSearchText(urlSearchText);
      }
    } else {
      // 普通模式：处理完整的过滤条件
      let hasChanges = false;
      
      // 处理搜索文本
      const urlSearchText = searchTextParam || '';
      if (urlSearchText !== currentSearchText) {
        setSearchText(urlSearchText);
        hasChanges = true;
      }
      
      // 处理类别
      if (selectedCategory) {
        const categories = selectedCategory.split(',');
        if (!categories.every(cat => currentSelectedCategories.includes(cat))) {
          setSelectedCategories(categories);
          hasChanges = true;
        }
      }
      
      // 处理品牌
      if (selectedBrand) {
        const brands = selectedBrand.split(',');
        if (!brands.every(brand => currentSelectedBrands.includes(brand))) {
          setSelectedBrands(brands);
          hasChanges = true;
        }
      }
      
      // 处理风格
      if (styleParam) {
        const styles = styleParam.split(',');
        if (!styles.every(style => currentSelectedStyles.includes(style))) {
          setSelectedStyles(styles);
          hasChanges = true;
        }
      }
      
      // 处理价格范围
      if (minPriceParam || maxPriceParam) {
        const minPrice = minPriceParam ? parseInt(minPriceParam) : 0;
        const maxPrice = maxPriceParam ? parseInt(maxPriceParam) : 10000;
        if (currentPriceRange[0] !== minPrice || currentPriceRange[1] !== maxPrice) {
          setPriceRange([minPrice, maxPrice]);
          hasChanges = true;
        }
      }
    }
    
    // 延迟重置标志，确保状态更新完成后再允许URL更新
    setTimeout(() => {
      isUpdatingFromURL.current = false;
    }, 100);
  }, [selectedCategory, selectedBrand, searchTextParam, styleParam, minPriceParam, maxPriceParam, isSearchMode]);

  // 更新URL参数的函数
  const updateURL = (searchText, brands, categories, styles, priceRange) => {
    const params = new URLSearchParams();
    
    // 根据当前模式处理搜索参数
    if (isSearchMode) {
      // 搜索模式：使用searchtext参数，始终保持（即使为空），并保持from=search标识
      params.set('searchtext', searchText ? searchText.trim() : '');
      params.set('from', 'search');
    } else {
      // 普通模式：使用searchtext参数，只有当搜索文本不为空时才添加
      if (searchText && searchText.trim()) {
        params.set('searchtext', searchText.trim());
      }
    }
    
    if (brands && brands.length > 0) {
      params.set('brand', brands.join(','));
    }
    if (categories && categories.length > 0) {
      params.set('category', categories.join(','));
    }
    if (styles && styles.length > 0) {
      params.set('style', styles.join(','));
    }
    if (priceRange && (priceRange[0] > 0 || priceRange[1] < 10000)) {
      params.set('minPrice', priceRange[0]);
      params.set('maxPrice', priceRange[1]);
    }

    const newURL = params.toString() ? `/products?${params.toString()}` : '/products';
    navigate(newURL, { replace: true });
  };

  // 用于防止URL更新循环的标志
  const isUpdatingFromURL = useRef(false);

  // 监听搜索条件变化，更新URL
  useEffect(() => {
    // 如果正在从URL更新状态，跳过URL更新
    if (isUpdatingFromURL.current) {
      return;
    }

    if (isSearchMode) {
      // 搜索模式：只更新搜索文本参数，添加防循环机制
      const timer = setTimeout(() => {
        const params = new URLSearchParams();
        params.set('searchtext', currentSearchText || '');
        const newURL = `/products?${params.toString()}`;
        
        // 只有当URL真的需要改变时才更新
        const currentURL = window.location.pathname + window.location.search;
        if (currentURL !== newURL) {
          navigate(newURL, { replace: true });
        }
      }, 300); // 增加延迟，避免频繁更新
      
      return () => clearTimeout(timer);
    } else {
      // 普通模式：更新所有过滤条件
      const timer = setTimeout(() => {
        updateURL(currentSearchText, currentSelectedBrands, currentSelectedCategories, currentSelectedStyles, currentPriceRange);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, isSearchMode ? [currentSearchText] : [currentSearchText, currentSelectedBrands, currentSelectedCategories, currentSelectedStyles, currentPriceRange]);

  const getFirstImage = (images) => {
    if (!images || typeof images !== 'string') {
      return '/images/placeholder.svg'; // 使用默认占位图
    }
    const firstImage = images.split('|')[0];
    return firstImage || '/images/placeholder.svg';
  };

  const getCategoryLabelLocal = (categoryId) => {
    const category = categoriesTrans.find(c => c.value === categoryId);
    return category ? category.label : '';
  };

  // IntersectionObserver 懒加载
  const loadMoreRef = useRef(null);
  const loadMoreCallbackRef = useRef(loadMore);
  
  // 更新 loadMore 回调引用
  useEffect(() => {
    loadMoreCallbackRef.current = loadMore;
  }, [loadMore]);
  
  useEffect(() => {
    if (!hasMore || loading) return;
    
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // 使用 ref 调用最新的 loadMore 函数
          loadMoreCallbackRef.current();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.1 }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading]); // 移除 loadMore 依赖

  return (
    <div>
      {/* <Title level={2}>滑雪装备库</Title> */}
      
      {/* 搜索模式：显示搜索输入框 */}
      {isSearchMode ? (
        <SearchSection>
          <Input
            size="large"
            placeholder="搜索名称/品牌/型号/性别/颜色/技术信息..."
            prefix={<SearchOutlined />}
            value={currentSearchText || ''}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ maxWidth: '100%' }}
          />
        </SearchSection>
      ) : (
        /* 普通模式：显示完整的过滤器 */
        <FilterSection>
          <Row gutter={[24, 24]}>
            <Col xs={24} md={6}>
              <Input
                placeholder="搜索名称/品牌/型号/性别/颜色/技术信息..."
                prefix={<SearchOutlined />}
                value={currentSearchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择品牌"
                value={currentSelectedBrands}
                onChange={setSelectedBrands}
                options={brands.map((brand) => ({ label: brand, value: brand }))}
                allowClear
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择类别"
                value={currentSelectedCategories}
                onChange={setSelectedCategories}
                options={categoriesTrans}
                allowClear
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="选择风格"
                value={currentSelectedStyles}
                onChange={setSelectedStyles}
                options={styles.map((style) => ({ label: style, value: style }))}
                allowClear
              />
            </Col>
            <Col xs={24}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5}>价格范围</Title>
                {(currentPriceRange[0] > 0 || currentPriceRange[1] < 10000) && (
                  <a onClick={() => setPriceRange([0, 10000])} style={{ fontSize: '12px' }}>
                    重置
                  </a>
                )}
              </div>
              <Slider
                range
                min={0}
                max={10000}
                value={currentPriceRange}
                onChange={setPriceRange}
                tooltip={{ formatter: (value) => `¥${value}` }}
              />
            </Col>
          </Row>
        </FilterSection>
      )}

      {showSkeleton && loading ? (
          <ProductGrid>
            <CardSkeleton count={18} />
          </ProductGrid>
      ) : equipments.length === 0 && !loading ? (
          <ProductGrid>
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              color: '#999'
            }}>
              <div style={{ fontSize: '43px', marginBottom: '16px', color: '#8B5CF6' }}>
                <SearchOutlined />
              </div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>没有找到相关装备</div>
              <div style={{ fontSize: '14px' }}>
                {isSearchMode ? '尝试使用其他关键词搜索' : '尝试调整筛选条件'}
              </div>
            </div>
          </ProductGrid>
      ) : (
        <ProductGrid>
          <Row gutter={[24, 24]}>
            {equipments.map((product) => (
              <Col xs={12} sm={8} md={6} lg={4} xl={4} xxl={3} key={product.id}>
                <StyledCard
                  hoverable
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/products/${product.id}`)}
                  cover={
                    <div 
                      style={{ position: 'relative', height: 160, marginTop: '10px' }}
                    >
                      <OptimizedImage
                        alt={product.name} 
                        src={getFirstImage(product.images)} 
                        width="100%"
                        height="100%"
                        style={{
                          objectFit: (product.year && (product.year.includes('24/25') || product.year.includes('25/26'))) ? 'contain' : 'cover'
                        }}
                        lazy={true}
                      />
                      <Tag
                        color="gold" 
                        style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          bottom: '10px',
                          zIndex: 1
                        }}
                      >
                        {product.year}款
                      </Tag>
                    </div>
                  }
                >
                  <StyledCard.Meta
                    title={
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center'
                      }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{product.name}</span>
                        <OptimizedImage 
                          src={getBrandImage(product.brandName)} 
                          alt={product.brandName}
                          width={24}
                          height={24}
                          style={{ 
                            objectFit: 'contain',
                            borderRadius: 4
                          }}
                          lazy={true}
                        />
                      </div>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          ¥{product.marketPrice}
                        </div>
                        <Space size={[0, 8]} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Tag color="blue">{product.brandName}</Tag>
                          <Tag color="cyan">{product.flex}</Tag>
                          <Tag color="purple">{getCategoryLabelLocal(product.category)}</Tag>
                        </Space>
                      </Space>
                    }
                  />
                </StyledCard>
              </Col>
            ))}
          </Row>
          {/* 懒加载底部提示 */}
         <div ref={loadMoreRef} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
           {hasMore ? (loading ? '加载中...' : '下拉加载更多') : `没有更多了，共找到 ${equipments.length} 件装备`}
         </div>
        </ProductGrid>
      )}
    </div>
  );
};

export default ProductList;